import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Star,
  CalendarClock,
  Users,
  ClipboardList,
  Utensils,
  FileText,
  HeartPulse,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Stethoscope,
  Mail,
  BookOpen,
  UserPlus,
  Bell,
  Wifi,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const navSections = [
  {
    label: "Core",
    items: [
      { path: "/admin/overview", label: "Overview", icon: LayoutDashboard },
      { path: "/admin/consultations", label: "Consultations", icon: CalendarClock },
      { path: "/admin/patients", label: "Patients", icon: Users },
    ],
  },
  {
    label: "Clinical",
    items: [
      { path: "/admin/assessments", label: "Assessments", icon: ClipboardList },
      { path: "/admin/meal-plans", label: "Meal Plans", icon: Utensils },
      { path: "/admin/reports", label: "Reports", icon: FileText },
      { path: "/admin/follow-ups", label: "Follow-ups & Triage", icon: HeartPulse },
    ],
  },
  {
    label: "Programme",
    items: [
      { path: "/admin/programme", label: "Programme", icon: Stethoscope },
      { path: "/admin/doctor-comms", label: "Doctor Comms", icon: Mail },
      { path: "/admin/communications", label: "Communications", icon: Bell },
      { path: "/admin/integrations", label: "Integrations", icon: Wifi },
    ],
  },
  {
    label: "Marketing",
    items: [
      { path: "/admin/newsletters", label: "Newsletters", icon: BookOpen },
      { path: "/admin/resources", label: "Free Resources", icon: FileText },
      { path: "/admin/subscribers", label: "Subscribers", icon: UserPlus },
      { path: "/admin/reviews", label: "Reviews", icon: Star },
    ],
  },
  {
    label: "System",
    items: [
      { path: "/admin/notifications", label: "Notifications", icon: Bell },
      { path: "/admin/settings", label: "Settings & Audit", icon: Settings },
    ],
  },
];

const StaffLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showLogout, setShowLogout] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin", { replace: true });
  };

  const sidebarWidth = collapsed ? "w-[68px]" : "w-[260px]";

  const SidebarContent = ({ onNavClick }) => (
    <div className="flex flex-col h-full" style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-stone-200/60 shrink-0">
        {!collapsed && (
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-7 w-auto" />
        )}
        {collapsed && (
          <img src="/platemind-logo-new.png" alt="PlateMind" className="h-7 w-7 object-contain object-left" />
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-stone-400 hover:bg-stone-100 hover:text-stone-600 transition-colors"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2.5">
        {navSections.map((section) => (
          <div key={section.label} className="mb-4">
            {!collapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold text-stone-400 uppercase tracking-wider">
                {section.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={onNavClick}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#FF7A00]/10 text-[#FF7A00]"
                        : "text-stone-600 hover:bg-stone-100 hover:text-stone-900"
                    } ${collapsed ? "justify-center px-2" : ""}`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <item.icon className="w-[18px] h-[18px] shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="shrink-0 border-t border-stone-200/60 p-3">
        {!collapsed && (
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#FF7A00]/10 flex items-center justify-center shrink-0">
              <span className="text-sm font-bold text-[#FF7A00]">
                {(user?.name || "S")[0].toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-900 truncate">{user?.name || "Staff"}</p>
              <p className="text-xs text-stone-400 truncate">{user?.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setShowLogout(true)}
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-stone-500 hover:bg-red-50 hover:text-red-600 transition-colors ${collapsed ? "justify-center px-2" : ""}`}
          data-testid="logout-btn"
        >
          <LogOut className="w-[18px] h-[18px]" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F7F5]" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="staff-dashboard">
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex fixed inset-y-0 left-0 z-40 ${sidebarWidth} bg-white border-r border-stone-200/60 flex-col transition-all duration-200`}>
        <SidebarContent />
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 h-14 bg-white border-b border-stone-200/60 flex items-center justify-between px-4">
        <button onClick={() => setMobileOpen(true)} className="p-2 text-stone-600" data-testid="mobile-sidebar-btn">
          <Menu className="w-5 h-5" />
        </button>
        <img src="/platemind-logo-new.png" alt="PlateMind" className="h-6 w-auto" />
        <div className="w-9" /> {/* Spacer for balance */}
      </header>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/30" onClick={() => setMobileOpen(false)} />
          <div className="relative w-[280px] bg-white h-full shadow-xl">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-4 right-4 p-1.5 text-stone-400 hover:text-stone-600"
            >
              <X className="w-5 h-5" />
            </button>
            <SidebarContent onNavClick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      {/* Main content */}
      <main className={`transition-all duration-200 pt-14 lg:pt-0 min-h-screen ${collapsed ? "lg:ml-[68px]" : "lg:ml-[260px]"}`}>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>

      {/* Logout dialog */}
      <AlertDialog open={showLogout} onOpenChange={setShowLogout}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to sign out of the staff dashboard?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout} className="bg-red-600 hover:bg-red-700">
              Sign out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffLayout;
